import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/cn";
import { CatalogoBusca, combina } from "./CatalogoBusca";
import { BlocosDeMateriais, chipCls } from "./BlocosDeMateriais";
import { CabecalhoDosMateriais } from "./CabecalhoDosMateriais";

/** `grupo` (família do material) separa as ofertas em blocos; sem ele, uma lista só. */
export type OfertaDeMaterial = { id: string; label: string; active?: boolean; grupo?: string };

type Props = {
  ofertas: OfertaDeMaterial[];
  /** Fora da vista até o "Ver todos" da família (o onboarding mostra primeiro os compatíveis na base). */
  outras?: OfertaDeMaterial[];
  materialIds: string[];
  onMaterialIds: (ids: string[]) => void;
  /** Material → escalas de cor. Só os materiais tocados entram. */
  escalasPorMaterial: Record<string, string[]>;
  onEscalasPorMaterial: (v: Record<string, string[]>) => void;
  escalas: { codigo: string; rotulo: string }[];
  /** Cria o material e já o marca neste serviço. `familia` vem do "Adicionar" dentro da família. */
  onNovoMaterial: (nome: string, familia?: string) => Promise<unknown> | void;
  criandoMaterial?: boolean;
  vazio?: string;
  /** Configurando vários serviços: materiais que só parte deles tem (clicar marca em todos). */
  parciais?: string[];
};

/**
 * Que materiais este serviço aceita e, pra cada um, em que escalas de cor ele sai — o núcleo do
 * dialog do serviço, sem servidor: o Catálogo e o onboarding dão as ofertas e guardam o resultado.
 *
 * A cor é declarada aqui porque é aqui que a decisão acontece (a coroa de zircônia só sai em VITA),
 * mas a declaração é **do material**: dois serviços que oferecem zircônia veem a mesma lista.
 */
export function MateriaisECoresDoServico({
  ofertas,
  outras = [],
  materialIds,
  onMaterialIds,
  escalasPorMaterial,
  onEscalasPorMaterial,
  escalas,
  onNovoMaterial,
  criandoMaterial,
  vazio,
  parciais,
}: Props) {
  const [busca, setBusca] = useState("");
  const marcados = [...ofertas, ...outras].filter((m) => materialIds.includes(m.id));

  /**
   * Quase sempre a mesma lista serve pra todos os materiais: a tela começa com UMA lista e quem
   * precisa distinguir expande. O mapa por material continua sendo a verdade.
   */
  const listas = marcados.map((m) => [...(escalasPorMaterial[m.id] ?? [])].sort().join("|"));
  const divergem = new Set(listas).size > 1;
  const [porMaterial, setPorMaterial] = useState(false);
  // Listas já diferentes não podem ser escondidas atrás de uma só.
  useEffect(() => {
    if (divergem) setPorMaterial(true);
  }, [divergem]);

  const comum = marcados.length > 0 ? (escalasPorMaterial[marcados[0]!.id] ?? []) : [];

  const alternarMaterial = (id: string) => {
    if (materialIds.includes(id)) {
      onMaterialIds(materialIds.filter((x) => x !== id));
      return;
    }
    onMaterialIds([...materialIds, id]);
    // Material novo herda a lista comum: nascer fora dela seria divergência que ninguém pediu.
    if (!porMaterial && comum.length > 0) onEscalasPorMaterial({ ...escalasPorMaterial, [id]: comum });
  };

  const alternarEscala = (materialId: string, codigo: string) => {
    const atuais = escalasPorMaterial[materialId] ?? [];
    onEscalasPorMaterial({ ...escalasPorMaterial, [materialId]: atuais.includes(codigo) ? atuais.filter((s) => s !== codigo) : [...atuais, codigo] });
  };

  /** No modo de lista única, mexer numa escala mexe em todos os materiais de uma vez. */
  const alternarComum = (codigo: string) => {
    const proxima = comum.includes(codigo) ? comum.filter((s) => s !== codigo) : [...comum, codigo];
    onEscalasPorMaterial({ ...escalasPorMaterial, ...Object.fromEntries(marcados.map((m) => [m.id, proxima])) });
  };

  /** Voltar pra lista única com listas diferentes achataria o que alguém declarou: pergunta e fica
   *  com a união — manter o que era permitido erra menos que apagar. */
  const voltarParaListaUnica = () => {
    if (divergem) {
      const uniao = [...new Set(marcados.flatMap((m) => escalasPorMaterial[m.id] ?? []))];
      if (!window.confirm("Os materiais têm listas de cor diferentes. Elas virarão uma lista só, com tudo que estava marcado. Continuar?")) return;
      onEscalasPorMaterial({ ...escalasPorMaterial, ...Object.fromEntries(marcados.map((m) => [m.id, uniao])) });
    }
    setPorMaterial(false);
  };

  const buscando = busca.trim() ? [...ofertas, ...outras].filter((m) => combina(m.label, busca)) : [];

  return (
    <>
      <div>
        <CabecalhoDosMateriais
          marcados={marcados}
          onTirar={alternarMaterial}
          onNovoMaterial={(nome) => onNovoMaterial(nome)}
          criandoMaterial={criandoMaterial}
        />

        {ofertas.length + outras.length > 8 && (
          <div className="mb-2">
            <CatalogoBusca valor={busca} onChange={setBusca} placeholder="Buscar material" achados={buscando.length} />
          </div>
        )}
        {busca.trim() ? (
          buscando.length ? (
            <BlocosDeMateriais ofertas={buscando} outras={[]} materialIds={materialIds} onAlternar={alternarMaterial} abertos parciais={parciais} />
          ) : (
            <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">Nenhum material com esse nome.</p>
          )
        ) : (
          <BlocosDeMateriais
            ofertas={ofertas}
            outras={outras}
            materialIds={materialIds}
            onAlternar={alternarMaterial}
            parciais={parciais}
            onNovoNaFamilia={(familia, novo) => void Promise.resolve(onNovoMaterial(novo, familia)).catch(() => undefined)}
          />
        )}
        {ofertas.length === 0 && vazio && <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">{vazio}</p>}
      </div>

      {marcados.length > 0 && escalas.length > 0 && (
        <div>
          <div className="mb-1 flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-zinc-600 dark:text-muted-foreground">Cores compatíveis</p>
            <button
              type="button"
              onClick={() => (porMaterial ? voltarParaListaUnica() : setPorMaterial(true))}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-brand-700 hover:underline dark:text-primary"
            >
              {porMaterial ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {porMaterial ? "Usar uma lista só" : "Definir por material"}
            </button>
          </div>

          <div className="flex flex-col gap-2 rounded-md border border-zinc-200 p-2 dark:border-border">
            {porMaterial ? (
              marcados.map((m) => (
                <div key={m.id}>
                  <p className="mb-1 text-[11px] font-semibold text-zinc-700 dark:text-foreground">{m.label}</p>
                  <div className="flex flex-wrap gap-1">
                    {escalas.map((e) => (
                      <button
                        key={e.codigo}
                        type="button"
                        aria-pressed={(escalasPorMaterial[m.id] ?? []).includes(e.codigo)}
                        onClick={() => alternarEscala(m.id, e.codigo)}
                        className={cn(chipCls((escalasPorMaterial[m.id] ?? []).includes(e.codigo)), "text-[11px]")}
                      >
                        {e.rotulo}
                      </button>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-wrap gap-1">
                {escalas.map((e) => (
                  <button
                    key={e.codigo}
                    type="button"
                    aria-pressed={comum.includes(e.codigo)}
                    onClick={() => alternarComum(e.codigo)}
                    className={cn(chipCls(comum.includes(e.codigo)), "text-[11px]")}
                  >
                    {e.rotulo}
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="mt-1 text-[11px] leading-snug text-zinc-500 dark:text-muted-foreground">
            Nenhuma escala marcada = todas as cores valem. Marcar uma escala diz que ela <strong className="font-semibold">só existe</strong> nos materiais
            marcados — vale para o material em qualquer serviço, não só neste. O dentista continua vendo as outras, com um aviso.
          </p>
        </div>
      )}
    </>
  );
}
